/**
 * ScoreScale — горизонтальная линейка диапазонов балла.
 *
 * Диапазоны идут слева направо от 0 до 100, засечка отмечает текущий балл,
 * подписи стоят под своими полосами. Линейка здесь не украшение: без
 * опубликованных границ красное «34 из 100» читается как «купи сервис, и
 * оценка вырастет». Показать, где проходит норма, дешевле любых
 * объяснений.
 *
 * Ширина каждой полосы пропорциональна её числовому размаху, поэтому
 * «0–50» занимает ровно половину линейки — шкала не врёт о пропорциях.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusS} from 'ui/tokens/ts/radii';

import {SCORE_ZONE_LABEL, SCORE_ZONE_TONE, paletteTone, type PaletteTone} from '../content';
import {TABULAR} from '../helpers';
import type {ScoreZone} from '../types';

export interface ScoreScaleProps {
  value: number;
  max: number;
  zones: ScoreZone[];
}

const Track = styled(Box)({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: 12,
  gap: spacingPx('xxs'),
});

const Band = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone; $span: number; $current: boolean}>(({theme, $tone, $span, $current}) => ({
  flex: `${$span} 1 0`,
  borderRadius: RadiusS,
  backgroundColor: theme.palette[$tone].main,
  opacity: $current ? 1 : 0.24,
}));

/** Засечка текущего балла — линия во всю высоту линейки и чуть за неё. */
const Marker = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$at: number}>(({$at}) => ({
  position: 'absolute',
  top: -4,
  bottom: -4,
  left: `${$at}%`,
  width: 2,
  backgroundColor: surfaceVar('onSurface'),
}));

const Labels = styled(Box)({
  display: 'flex',
  width: '100%',
  gap: spacingPx('xxs'),
});

const Label = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$span: number; $current: boolean}>(({$span, $current}) => ({
  flex: `${$span} 1 0`,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: spacingPx('tight'),
  color: $current ? surfaceVar('onSurface') : surfaceVar('onSurfaceMuted'),
}));

const RangeName = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$current: boolean}>(({$current}) => ({
  ...text($current ? 'label.m' : 'body.m'),
}));

const RangeNumbers = styled(Box)({
  ...text('body.m'),
  ...TABULAR,
  color: surfaceVar('onSurfaceMuted'),
});

export function ScoreScale({value, max, zones}: ScoreScaleProps) {
  // Границы включительные: полоса 46–60 занимает пятнадцать делений, а не
  // четырнадцать, иначе на стыках линейка теряет по пункту.
  const span = (zone: ScoreZone) => zone.to - zone.from + 1;
  const isCurrent = (zone: ScoreZone) => value >= zone.from && value <= zone.to;

  return (
    <Stack gap='s'>
      <Track>
        {zones.map(zone => (
          <Band
            key={zone.key}
            $tone={paletteTone(SCORE_ZONE_TONE[zone.key])}
            $span={span(zone)}
            $current={isCurrent(zone)}
          />
        ))}
        <Marker $at={(value / max) * 100} />
      </Track>

      <Labels>
        {zones.map(zone => (
          <Label key={zone.key} $span={span(zone)} $current={isCurrent(zone)}>
            <RangeName $current={isCurrent(zone)}>{SCORE_ZONE_LABEL[zone.key]}</RangeName>
            <RangeNumbers>
              {zone.from}–{zone.to}
            </RangeNumbers>
          </Label>
        ))}
      </Labels>
    </Stack>
  );
}
